import type { AdminOrderPaymentDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoGrid, InfoItem } from './InfoBlocks';
import { PaymentAmounts } from './PaymentAmounts';
import { PaymentHoldWindow } from './PaymentHoldWindow';
import { PaymentStatusChip } from './PaymentStatusChip';
import { formatCurrency, formatDateTime } from '../../../utils/format';

/**
 * AUT-219 — the read half of the Платёж tab's payment block: status, the two
 * money figures, the hold clock, card + settlement timestamps.
 *
 * Composition only; each concern lives in its own component so the "two
 * distinct amounts" rule and the "expired hold is flagged" rule are each
 * testable in isolation. The summary is already sanitised server-side (card
 * last-4 only, never PAN / recurring_token — architect lock AUT-112), so no
 * PII gating happens here.
 *
 * Note the deliberate label split: `capturedAt` is "Дата списания" (a
 * timestamp), while the money figure is "Фактически списано". Two things named
 * "Списано" on one screen is how the wrong number gets read as the right one.
 */
const cardLabel = (last4: string | null, brand: string | null): string => {
  if (!last4) return '—';
  return brand ? `${brand} ···· ${last4}` : `···· ${last4}`;
};

export const PaymentSummary = ({ payment }: { payment: AdminOrderPaymentDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      <InfoGrid>
        <InfoItem label={t('orders.detail.payment.status')}>
          <PaymentStatusChip status={payment.status} />
        </InfoItem>
        <InfoItem label={t('orders.detail.payment.card')}>
          {cardLabel(payment.cardLast4, payment.cardBrand)}
        </InfoItem>
      </InfoGrid>

      <PaymentAmounts payment={payment} />

      <PaymentHoldWindow payment={payment} />

      <InfoGrid>
        <InfoItem label={t('orders.detail.payment.capturedAt')}>
          {formatDateTime(payment.capturedAt)}
        </InfoItem>
        <InfoItem label={t('orders.detail.payment.refundedTotal')}>
          {formatCurrency(Number(payment.refundedTotal), payment.currency)}
        </InfoItem>
      </InfoGrid>
    </Box>
  );
};
